<template>
  <div class="add-blog-container">
    <div v-if="!userIsAdmin" class="no-access text-center">
      <h1>Access Denied</h1>
      <p>Think this is an error? Please reach out to the Ancestral Homes Network team directly.</p>
    </div>
    <div v-else>
      <Alert v-if="resultMessage" :title="resultMessage" :alert-type="alertType" floating/>
      <h1>{{ isEditMode ? 'Edit Blog Post' : 'Add Blog Post' }}</h1>
      <p>Posts made here will appear in the blog section of the site. Use the toolbar to format your text and add images. Once posted, admin users can also delete blog posts from the main blog page.</p>
      <div class="blog-form row">
        <div class="col-12">
          <label for="title" class="form-label">Title</label>
          <input v-model="title" type="text" class="form-control" id="title">
        </div>
        <div class="col-12">
          <label for="author" class="form-label">Author (optional)</label>
          <input v-model="author" type="text" class="form-control" id="author">
        </div>
        <div class="col-12">
          <label for="text" class="form-label">Content</label>
          <RichTextEditor v-model="text" @imageUploadError="onImageUploadError"/>
        </div>
      </div>
      <div class="action-buttons">
        <button
            :class="['btn', 'btn-lg', 'btn-secondary', 'publish-btn', {'btn-disabled': isLoading || !text}]"
            @click="showPreview = !showPreview"
            :disabled="!text || !title"
        >
          {{ showPreview ? 'Hide Preview' : 'Preview' }}
        </button>
        <button
            :class="['btn', 'btn-lg', 'btn-primary', 'publish-btn', {'btn-disabled': isLoading || !text || !title}]"
            @click="publishPost"
            :disabled="!text || !title"
        >
          {{ isEditMode ? 'Edit' : 'Publish' }}
        </button>
      </div>
      <div v-if="showPreview && text" class="preview-container page-bg light-teal-bg">
        <h1>{{ title }}</h1>
        <p v-if="author" class="text-muted">By {{ author }}</p>
        <hr>
        <MarkdownRenderer :source="text"/>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { submitStatus } from '@/helpers/utils';
import Alert from '@/components/Alert.vue';
import MarkdownRenderer from '@/components/MarkdownRenderer.vue';
import RichTextEditor from '@/components/RichTextEditor.vue';

export default {
  name: "AddBlogPost",
  components: { MarkdownRenderer, RichTextEditor, Alert },
  data() {
    return {
      title: null,
      author: null,
      text: null,
      resultMessage: null,
      showPreview: false,
      photoUploadFailed: false,
      isEditMode: false,
      editingPostId: null,
    }
  },
  mounted() {
    if (this.postToEdit) {
      this.title = this.postToEdit.title;
      this.author = this.postToEdit.author;
      this.text = this.postToEdit.text;
      this.editingPostId = this.postToEdit._id;
      this.isEditMode = true;
      this.setPostToEdit(null); // consume once so returning later starts fresh
    }
  },
  watch: {
    resultMessage(to) {
      if (to) {
        // remove after a few seconds
        setTimeout(() => {
          this.resultMessage = null;
          this.photoUploadFailed = false;
        }, 3000);
      }
    },
    blogStatus(to) {
      if (to && to !== submitStatus.LOADING) {
        const prefix = this.isEditMode ? 'edit' : 'publish';
        this.resultMessage = to === submitStatus.SUCCESS
            ? `Blog post ${prefix}ed successfully!`
            : `Failed to ${prefix} blog post. Please ensure that title and text are both filled out.`;
      }
    }
  },
  computed: {
    ...mapGetters(['blogStatus', 'userSettings', 'postToEdit']),
    userIsAdmin() {
      return this.$auth0.isAuthenticated && this.userSettings.is_admin;
    },
    isLoading() {
      return this.blogStatus === submitStatus.LOADING;
    },
    alertType() {
      return this.photoUploadFailed || this.blogStatus === submitStatus.FAILED ? 'danger' : 'success';
    },
  },
  methods: {
    ...mapActions(['publishBlogPost', 'updateBlogPost', 'setPostToEdit']),
    publishPost() {
      if (this.isEditMode) {
        const postContents = {
          title: this.title,
          author: this.author,
          text: this.text,
        }
        this.updateBlogPost({ id: this.editingPostId, postContents });
      } else {
        const post = {
          title: this.title,
          author: this.author,
          publishDate: new Date(),
          text: this.text,
        }
        this.publishBlogPost(post);
      }
      this.clearForm();
    },
    clearForm() {
      this.title = null;
      this.author = null;
      this.text = null;
    },
    onImageUploadError() {
      this.photoUploadFailed = true;
      this.resultMessage = 'There was an error uploading the image.';
    }
  }
}
</script>

<style scoped>
.add-blog-container {
  padding: 2rem;
}

.form-control {
  margin-bottom: 1rem;
}

.publish-btn {
  margin: 0;
}
.action-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin: 1rem 0;
}

.preview-container {
  padding: 2rem;
  margin-bottom: 2rem;
  border-radius: 4px;
  overflow: auto;
}
</style>